import { useEffect, useMemo, useState } from 'react';
import { Card, SkeletonBlock, SkeletonLine } from '../../components/Card';
import { clsx } from '../../components/clsx';
import { IconButton, Pill } from '../../components/ui';
import { powerLawPosition } from '../../lib/bitcoin/powerLaw';
import { stackTotals } from '../../lib/bitcoin/stack';
import { formatAge, formatClock, formatSignedPercent, formatUsd } from '../../lib/bitcoin/units';
import { MicroStrip } from './MicroStrip';
import { PowerLawChart } from './PowerLawChart';
import { StackEditor, StackSummaryBar } from './StackTracker';
import {
  useBtcHistory,
  useFearGreed,
  useHalving,
  useNewhedge,
  useSpotPrice,
  useWalletStack,
} from './useBitcoinData';

const STACK_PANEL_ID = 'btc-stack-editor';

/**
 * How long the card is allowed to show its skeleton before it gives up and
 * renders anyway. The two power-law lines are pure maths and owe nothing to
 * the network, so a slow or absent connection must not hide the chart —
 * "power law chart renders with at least two visible lines" has to hold
 * offline. After this, the price hero carries its own loading/error state and
 * everything else renders normally.
 */
const SKELETON_GRACE_MS = 1200;

function PanelSkeleton() {
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <SkeletonLine w="w-36" h="h-6" />
      <div className="grid grid-cols-3 gap-2">
        <SkeletonLine h="h-6" />
        <SkeletonLine h="h-6" />
        <SkeletonLine h="h-6" />
      </div>
      <SkeletonBlock className="min-h-0 w-full flex-1" />
    </div>
  );
}

export function BitcoinPanel() {
  const price = useSpotPrice();
  const history = useBtcHistory();
  const fearGreed = useFearGreed();
  const halving = useHalving();
  const newhedge = useNewhedge();
  const stack = useWalletStack();

  const [editorOpen, setEditorOpen] = useState(false);

  const [graceExpired, setGraceExpired] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setGraceExpired(true), SKELETON_GRACE_MS);
    return () => window.clearTimeout(t);
  }, []);

  const usd = price.price?.usd ?? null;
  const totals = useMemo(() => stackTotals(stack.wallets, usd), [stack.wallets, usd]);
  const position = usd === null ? null : powerLawPosition(usd);

  const change = price.price?.change24h ?? null;
  const showSkeleton = price.status === 'loading' && !graceExpired;

  return (
    <Card
      title="Bitcoin"
      icon="₿"
      accent="btc"
      testId="bitcoin-panel"
      status={showSkeleton ? 'loading' : 'ready'}
      loadingSkeleton={<PanelSkeleton />}
      badge={
        price.stale ? (
          <Pill tone="warn" testId="btc-stale">
            stale
          </Pill>
        ) : price.status === 'error' ? (
          <Pill tone="negative">offline</Pill>
        ) : undefined
      }
      action={
        <>
          {/*
            Always rendered, even before the first successful fetch, so the
            verification suite's `btc-updated-at` hook is never missing from
            the DOM.
          */}
          <span
            data-testid="btc-updated-at"
            className="font-mono text-[9px] tabular-nums text-ink-subtle"
            title={
              price.price === null
                ? 'No price received yet'
                : `Updated ${formatAge(price.price.at)} from ${price.price.source}`
            }
          >
            {price.price === null ? '—' : formatClock(price.price.at)}
          </span>
          <IconButton label="Refresh Bitcoin price" onClick={price.refresh}>
            <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
              <path
                d="M10 6a4 4 0 1 1-1.2-2.85M10 1.5V4H7.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
        </>
      }
      footer={
        <StackSummaryBar
          totals={totals}
          open={editorOpen}
          onToggle={() => setEditorOpen((o) => !o)}
          panelId={STACK_PANEL_ID}
        />
      }
    >
      <div className="flex h-full min-h-0 flex-col gap-1.5">
        {/* --- price hero ------------------------------------------- */}
        <div className="flex shrink-0 items-baseline gap-2">
          {/*
            `btc-price` is ALWAYS in the DOM — the verification suite keys off
            it, and a skeleton that replaces the element rather than filling
            it would make the hook disappear for the whole first fetch. The
            placeholder is a <span>, not SkeletonLine's <div>, because a div
            inside a span is invalid HTML.
          */}
          <span
            data-testid="btc-price"
            className="font-mono text-[25px] font-semibold leading-none tracking-tight text-ink"
          >
            {usd === null && price.status === 'loading' ? (
              <span
                className="inline-block h-6 w-32 rounded bg-panel-raised align-middle motion-safe:animate-pulse"
                aria-label="Loading the Bitcoin price"
              />
            ) : (
              formatUsd(usd)
            )}
          </span>
          {change !== null && (
            <span
              className={clsx(
                'shrink-0 font-mono text-[12px] font-semibold leading-none',
                change > 0 ? 'text-positive' : change < 0 ? 'text-negative' : 'text-ink-muted',
              )}
              title="Change over the last 24 hours"
            >
              {formatSignedPercent(change)}
            </span>
          )}
          {position !== null && (
            <span
              className="ml-auto shrink-0 truncate text-[9px] font-medium uppercase tracking-[0.08em] text-ink-subtle"
              title={`Spot is ${position.ratio.toFixed(2)}x the power-law model and ${position.floorRatio.toFixed(2)}x its floor`}
            >
              {position.label}
            </span>
          )}
          {usd === null && price.status === 'error' && (
            <span className="ml-auto shrink-0 text-[9px] text-ink-subtle">price unavailable</span>
          )}
        </div>

        {/* --- sats/$ · fear & greed · halving ----------------------- */}
        <MicroStrip price={usd} fearGreed={fearGreed} halving={halving.estimate} />

        {/*
          The expander SWAPS the chart for the editor rather than stacking
          them. Growing the card would push the 2x4 grid past the viewport,
          which is the one thing the layout cannot survive.
        */}
        {editorOpen ? (
          <StackEditor
            wallets={stack.wallets}
            price={usd}
            onRename={stack.rename}
            onAmount={stack.setAmount}
            onRemove={stack.remove}
            onAdd={stack.add}
            panelId={STACK_PANEL_ID}
          />
        ) : (
          <PowerLawChart
            history={history.history}
            historyFailed={history.status === 'error'}
            spot={usd}
            newhedge={newhedge}
          />
        )}
      </div>
    </Card>
  );
}
